import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode
} from 'react';
import type { Theme } from './types';
import { themeRegistry } from './registry';
import { writeStoredThemeSlug } from './storage';

type ThemeContextValue = {
  theme: Theme;
  /**
   * Switch the active theme by slug. On hit: updates state, persists the
   * slug to `localStorage['axi.theme']`, and lets the effect below mirror
   * the slug onto `<html data-axi-theme>`. Unknown slug is a no-op with
   * a `console.warn`. Signature is locked so a future `<ThemeSwitcher/>`
   * component can call it unchanged.
   */
  setTheme: (slug: string) => void;
};

/**
 * Exported so consumers (e.g. gallery pages that should still work
 * when rendered outside `<ThemeProvider>`) can read the context
 * directly without throwing.
 */
export const ThemeContext = createContext<ThemeContextValue | null>(null);

export type ThemeProviderProps = {
  /** The theme captured at App's mount; the provider does not re-resolve. */
  initialTheme: Theme;
  children: ReactNode;
};

/**
 * Provides the active theme via React Context, mirrors it to
 * `<html data-axi-theme>` for CSS / debugging, and persists it across
 * reloads via `localStorage`. The persistence write and the DOM attribute
 * are the responsibility of this provider — consumers only call
 * `setTheme(slug)` or read `theme`.
 *
 * The provider itself does not read `window.localStorage`; that is done
 * once by `resolveInitialThemeSlug()` at App's mount and the result is
 * passed in as `initialTheme`.
 */
export function ThemeProvider({ initialTheme, children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(initialTheme);

  // Mirror the active slug onto the document root so:
  //   1. CSS can scope rules under `[data-axi-theme="<slug>"]`.
  //   2. DevTools / browser inspectors see a single source of truth.
  // The inline anti-FOUC script in index.html sets the same attribute
  // synchronously before React mounts, so this effect just keeps them
  // in sync afterwards.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-axi-theme', theme.slug);
  }, [theme.slug]);

  const setTheme = (slug: string) => {
    const next = themeRegistry.get(slug);
    if (next) {
      setThemeState(next);
      writeStoredThemeSlug(slug);
      return;
    }
    if (typeof console !== 'undefined') {
      console.warn(
        `[theme] unknown slug "${slug}", keeping active theme "${theme.slug}"`
      );
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Read the active theme from context. Throws when used outside a
 * `<ThemeProvider>` so mis-mounts fail loudly.
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme() must be used within <ThemeProvider>');
  }
  return ctx;
}